import { filterRealPersonaPlatforms } from '../lib/platforms';
import { redactedJson } from '../lib/redaction';

export type WorkbenchStatusTone = 'green' | 'blue' | 'yellow' | 'red' | 'muted';

export type WorkbenchStatusItem = {
  label: string;
  value: string;
  evidence: string;
  tone: WorkbenchStatusTone;
};

export type PersonaWorkbenchActionKey = 'validate' | 'preview' | 'create_version' | 'publish' | 'rollback' | 'discard' | 'reset';
export type PersonaWorkbenchConfirmableActionKey = Extract<PersonaWorkbenchActionKey, 'create_version' | 'publish'>;

export type PersonaWorkbenchConfirmation = {
  key: PersonaWorkbenchConfirmableActionKey;
  confirmationKey: string;
};

export type PersonaWorkbenchAction = {
  key: PersonaWorkbenchActionKey;
  label: string;
  labelKey: string;
  backend: string;
  backendKey?: string;
  available: boolean;
  preview: string;
  previewKey: string;
  confirmation: string;
  confirmationTextKey: string;
  copyValues?: Record<string, string | number>;
  targetVersion?: number;
  confirmationKey?: string;
};

export type PersonaWorkbenchSources = {
  personaId: string;
  persona?: unknown;
  config?: unknown;
  status?: unknown;
  preview?: unknown;
  validation?: unknown;
  promptAssignments?: unknown;
  sessions?: unknown;
  selectedSession?: unknown;
  diff?: unknown;
  snapshot?: unknown;
  versions?: unknown;
  audit?: unknown;
  errors?: Record<string, string>;
};

export type PersonaConfigCenterModel = {
  runtimeRows: Array<[string, string]>;
  consoleRows: Array<[string, string]>;
  bindingRows: Array<[string, string]>;
  layerRows: Array<[string, string]>;
  configJson: string;
  draftJson: string;
};

export type PersonaWorkbenchModel = {
  personaId: string;
  configId: string;
  platforms: string[];
  header: {
    title: string;
    description: string;
    meta: Array<[string, string]>;
  };
  status: WorkbenchStatusItem[];
  actions: PersonaWorkbenchAction[];
  promptAssignments: {
    enabledCount: number;
    totalCount: number;
    draftRevision: string;
    draftChanged: boolean;
    selectedScope: string;
    moduleSource: string;
    injectionOrder: string[];
    modules: Array<Record<string, unknown>>;
  };
  configCenter: PersonaConfigCenterModel;
  runtimeEvidenceRows: Array<[string, string]>;
  sessions: Array<{
    id: string;
    name: string;
    platform: string;
    active: boolean;
    live: boolean;
    historyCount: number;
    runtimeProfileId: string;
    codexThreadId: string;
    updatedAt: string;
  }>;
  rawJson: string;
};

export function buildPersonaWorkbenchModel(sources: PersonaWorkbenchSources): PersonaWorkbenchModel {
  const persona = asRecord(sources.persona);
  const config = asRecord(sources.config);
  const manifest = asRecord(config.manifest);
  const draft = asRecord(config.draft);
  const draftSpec = asRecord(draft.spec);
  const status = asRecord(sources.status);
  const preview = asRecord(sources.preview);
  const assignments = asRecord(sources.promptAssignments);
  const sessionRows = getArray(asRecord(sources.sessions).sessions);
  const versions = getArray(asRecord(sources.versions).versions).map(item => asRecord(item));
  const errors = sources.errors ?? {};
  const configId = getString(asRecord(persona.control_plane).persona_config_id) || getString(manifest.id);
  const publishedVersion = getNumber(manifest.published_version) || getNumber(asRecord(status.published).version);
  const platforms = extractPlatforms(persona.platforms);
  const sessions = sessionRows.map(mapSessionRow);
  const liveSessions = getNumber(persona.live_sessions_count) || sessions.filter(session => session.live).length;
  const totalSessions = getNumber(persona.sessions_count) || sessions.length;
  const assignmentModules = getArray(assignments.modules).map(item => asRecord(item));
  const enabledCount = assignmentModules.filter(module => getBool(module.enabled)).length;
  const draftChanged = getBool(assignments.draft_changed) || getBool(status.draft_changed);
  const runtimeEffect = asRecord(status.runtime_effect);
  const runtimeLayer = asRecord(status.runtime);
  const runtimeLoaded = getBool(runtimeEffect.runtime_loaded) || getBool(runtimeLayer.loaded) || getBool(runtimeLayer.runtime_loaded);
  const runtimeApplied = getBool(runtimeEffect.live_applied) || getBool(runtimeLayer.live_applied);
  const restartRequired = getBool(runtimeEffect.restart_required) || getBool(status.restart_required);
  const provider = getString(personaRuntime(persona).active_provider) || 'Unavailable';
  const model = getString(personaRuntime(persona).current_model) || 'Unavailable';
  const draftRevision = getString(asRecord(config.draft).revision) || getString(asRecord(status.draft).revision) || getString(preview.revision);
  const actions = buildActionStates({ personaId: sources.personaId, draftRevision, latestVersion: latestVersionNumber(versions), errors, preview });
  const configCenter = buildConfigCenter({ persona, config, draft, draftSpec, assignments, sources });

  return {
    personaId: sources.personaId,
    configId,
    platforms,
    header: {
      title: getString(manifest.display_name) || getString(persona.name) || sources.personaId,
      description: getString(manifest.description) || 'Live Persona resource detail for configuration, prompt assignment, runtime evidence, and history.',
      meta: [
        ['Kind', 'Persona'],
        ['Persona ID', getString(manifest.project_name) || getString(persona.name) || sources.personaId],
        ['Agent', getString(persona.agent_type) || getString(asRecord(asRecord(config.draft).spec).agent_type) || 'Unavailable'],
        ['Config ID', configId || 'Unavailable'],
        ['Published', publishedVersion > 0 ? `v${publishedVersion}` : 'Unpublished'],
      ],
    },
    status: [
      {
        label: 'Runtime',
        value: runtimeApplied ? 'Live applied' : runtimeLoaded ? 'Loaded' : 'Unavailable',
        evidence: restartRequired ? 'Restart required' : `${provider} / ${model}`,
        tone: restartRequired ? 'yellow' : runtimeLoaded || runtimeApplied ? 'green' : 'muted',
      },
      {
        label: 'Sessions',
        value: `${liveSessions}/${totalSessions} live`,
        evidence: 'RuntimeProfile + thread evidence',
        tone: liveSessions > 0 ? 'green' : totalSessions > 0 ? 'yellow' : 'muted',
      },
      {
        label: 'Prompt draft',
        value: draftChanged ? 'Changed' : 'Clean',
        evidence: `revision ${getString(assignments.draft_revision) || getString(asRecord(config.draft).revision) || 'unavailable'}`,
        tone: draftChanged ? 'yellow' : 'green',
      },
      {
        label: 'Platforms',
        value: platforms.length ? platforms.join(', ') : 'Unavailable',
        evidence: 'Mode controls excluded',
        tone: platforms.length ? 'blue' : 'muted',
      },
    ],
    actions,
    promptAssignments: {
      enabledCount,
      totalCount: assignmentModules.length,
      draftRevision: getString(assignments.draft_revision),
      draftChanged,
      selectedScope: formatScopeLabel(getString(assignments.platform)),
      moduleSource: getString(asRecord(assignments.prompt_module_source).source) || 'Unavailable',
      injectionOrder: getArray(assignments.injection_order).map(String),
      modules: assignmentModules,
    },
    configCenter,
    runtimeEvidenceRows: buildRuntimeEvidenceRows(status),
    sessions,
    rawJson: redactedJson(sources),
  };
}

export function canRunPersonaWorkbenchAction(
  action: PersonaWorkbenchAction | undefined,
  confirmation: PersonaWorkbenchConfirmation | null,
  loading: boolean,
) {
  if (loading || !action?.available || !isRunnableAction(action.key)) return false;
  if (!isConfirmableAction(action.key)) return true;
  return Boolean(
    action.confirmationKey
    && confirmation?.key === action.key
    && confirmation.confirmationKey === action.confirmationKey,
  );
}

function buildActionStates({
  personaId,
  draftRevision,
  latestVersion,
  errors,
  preview,
}: {
  personaId: string;
  draftRevision: string;
  latestVersion: number;
  errors: Record<string, string>;
  preview: Record<string, unknown>;
}): PersonaWorkbenchAction[] {
  const draftText = draftRevision || 'unavailable';
  const copyValues = { draftRevision: draftText, targetVersion: latestVersion };
  const hasDraft = draftRevision !== '';
  const missingPreviewMessage = 'Preview payload has not loaded from /persona_configs/{id}/preview.';
  const hasPreview = Object.keys(preview).length > 0 && !errors['config preview'];
  const validateUnavailable = errors['config validate'] || (!hasDraft ? 'No draft revision returned by /persona_configs/{id}.' : '');
  const previewUnavailable = errors['config preview'] || (!hasPreview ? missingPreviewMessage : '');
  const versionUnavailable = !hasDraft
    ? 'No draft revision returned by /persona_configs/{id}.'
    : !hasPreview
      ? missingPreviewMessage
      : '';
  const publishUnavailable = latestVersion <= 0
    ? 'Unavailable because /versions returned no immutable version to publish.'
    : !hasPreview
      ? missingPreviewMessage
      : '';

  return [
    {
      key: 'validate',
      label: 'Validate draft',
      labelKey: 'modules.personaWorkbench.actions.validate.label',
      backend: 'POST /persona_configs/{id}/validate',
      available: !validateUnavailable,
      previewKey: validateUnavailable ? 'modules.personaWorkbench.actions.validate.unavailableNoDraft' : 'modules.personaWorkbench.actions.validate.preview',
      preview: validateUnavailable || `Current draft revision ${draftText} can be validated without rendering secret values.`,
      confirmationTextKey: validateUnavailable ? 'modules.personaWorkbench.actions.validate.unavailableUntilDraft' : 'modules.personaWorkbench.actions.validate.confirmation',
      confirmation: validateUnavailable ? 'Unavailable until a draft exists.' : 'Not required; validation is read-only.',
      copyValues,
    },
    {
      key: 'preview',
      label: 'Preview draft',
      labelKey: 'modules.personaWorkbench.actions.preview.label',
      backend: 'GET /persona_configs/{id}/preview',
      available: !previewUnavailable,
      previewKey: previewUnavailable ? 'modules.personaWorkbench.actions.preview.unavailableMissing' : 'modules.personaWorkbench.actions.preview.preview',
      preview: previewUnavailable || `Preview contract loaded for draft revision ${draftText} with redacted summary fields.`,
      confirmationTextKey: previewUnavailable ? 'modules.personaWorkbench.actions.preview.unavailableUntilPreview' : 'modules.personaWorkbench.actions.preview.confirmation',
      confirmation: previewUnavailable ? 'Unavailable until preview payload loads.' : 'Not required; preview is read-only.',
      copyValues,
    },
    {
      key: 'create_version',
      label: 'Create version',
      labelKey: 'modules.personaWorkbench.actions.createVersion.label',
      backend: 'POST /persona_configs/{id}/versions',
      available: !versionUnavailable,
      confirmationKey: !versionUnavailable ? `${personaId}:create_version:draft-r${draftText}` : undefined,
      previewKey: versionUnavailable
        ? (hasDraft ? 'modules.personaWorkbench.actions.preview.unavailableMissing' : 'modules.personaWorkbench.actions.validate.unavailableNoDraft')
        : 'modules.personaWorkbench.actions.createVersion.preview',
      preview: versionUnavailable || `Creates an immutable version from current draft revision ${draftText}.`,
      confirmationTextKey: versionUnavailable
        ? (hasDraft ? 'modules.personaWorkbench.actions.createVersion.unavailableUntilPreviewEvidence' : 'modules.personaWorkbench.actions.validate.unavailableUntilDraft')
        : 'modules.personaWorkbench.actions.createVersion.confirmation',
      confirmation: versionUnavailable
        ? (hasDraft ? 'Unavailable until preview evidence loads.' : 'Unavailable until a draft exists.')
        : 'Required before creating a version.',
      copyValues,
    },
    {
      key: 'publish',
      label: 'Publish latest version',
      labelKey: 'modules.personaWorkbench.actions.publish.label',
      backend: 'POST /persona_configs/{id}/publish',
      available: !publishUnavailable,
      targetVersion: latestVersion || undefined,
      confirmationKey: !publishUnavailable ? `${personaId}:publish:v${latestVersion}` : undefined,
      previewKey: !publishUnavailable
        ? 'modules.personaWorkbench.actions.publish.preview'
        : latestVersion > 0 ? 'modules.personaWorkbench.actions.preview.unavailableMissing' : 'modules.personaWorkbench.actions.publish.unavailableNoVersion',
      preview: !publishUnavailable
        ? `Publishes latest immutable version v${latestVersion}; runtime effect must be reviewed after publish.`
        : publishUnavailable,
      confirmationTextKey: !publishUnavailable
        ? 'modules.personaWorkbench.actions.publish.confirmation'
        : latestVersion > 0 ? 'modules.personaWorkbench.actions.createVersion.unavailableUntilPreviewEvidence' : 'modules.personaWorkbench.actions.publish.unavailableUntilVersion',
      confirmation: !publishUnavailable
        ? `Required before publishing v${latestVersion}.`
        : latestVersion > 0 ? 'Unavailable until preview evidence loads.' : 'Unavailable until a publishable version exists.',
      copyValues,
    },
    {
      key: 'rollback',
      label: 'Rollback version',
      labelKey: 'modules.personaWorkbench.actions.rollback.label',
      backend: 'POST /persona_configs/{id}/rollback',
      available: false,
      previewKey: 'modules.personaWorkbench.actions.rollback.preview',
      preview: 'Unavailable because no backend rollback preview contract exists.',
      confirmationTextKey: 'modules.personaWorkbench.actions.rollback.confirmation',
      confirmation: 'Unavailable; rollback requires preview plus explicit confirmation before runtime mutation.',
    },
    {
      key: 'discard',
      label: 'Discard draft',
      labelKey: 'modules.personaWorkbench.actions.discard.label',
      backend: 'Contract gap: no discard draft endpoint exists.',
      backendKey: 'modules.personaWorkbench.actions.discard.backendGap',
      available: false,
      previewKey: 'modules.personaWorkbench.actions.discard.preview',
      preview: 'Unavailable because no backend discard preview contract exists.',
      confirmationTextKey: 'modules.personaWorkbench.actions.contractGapConfirmation',
      confirmation: 'Unavailable; no safe backend mutation contract.',
    },
    {
      key: 'reset',
      label: 'Reset to published',
      labelKey: 'modules.personaWorkbench.actions.reset.label',
      backend: 'Contract gap: no reset-to-published endpoint exists.',
      backendKey: 'modules.personaWorkbench.actions.reset.backendGap',
      available: false,
      previewKey: 'modules.personaWorkbench.actions.reset.preview',
      preview: 'Unavailable because no backend reset preview contract exists.',
      confirmationTextKey: 'modules.personaWorkbench.actions.contractGapConfirmation',
      confirmation: 'Unavailable; no safe backend mutation contract.',
    },
  ];
}

function isRunnableAction(key: PersonaWorkbenchActionKey) {
  return key === 'validate' || key === 'preview' || key === 'create_version' || key === 'publish';
}

function isConfirmableAction(key: PersonaWorkbenchActionKey): key is PersonaWorkbenchConfirmableActionKey {
  return key === 'create_version' || key === 'publish';
}

function personaRuntime(persona: Record<string, unknown>) {
  return asRecord(persona.runtime);
}

function buildConfigCenter({
  persona,
  config,
  draft,
  draftSpec,
  assignments,
  sources,
}: {
  persona: Record<string, unknown>;
  config: Record<string, unknown>;
  draft: Record<string, unknown>;
  draftSpec: Record<string, unknown>;
  assignments: Record<string, unknown>;
  sources: PersonaWorkbenchSources;
}): PersonaConfigCenterModel {
  const runtime = personaRuntime(persona);
  const agent = asRecord(draftSpec.agent);
  const options = asRecord(agent.options);
  const display = asRecord(draftSpec.display);
  const style = asRecord(draftSpec.style);
  const platformDefaults = asRecord(agent.platform_defaults);
  const platformOverrides = asRecord(draftSpec.platform_overrides);
  const providers = getArray(agent.providers).map(item => asRecord(item));
  const activeProviderName = getString(agent.provider) || getString(runtime.active_provider);
  const activeProvider = providers.find(provider => getString(provider.name) === activeProviderName) || providers[0] || {};
  const activeModel = getString(agent.model) || getString(runtime.current_model) || getString(activeProvider.model);
  const skills = getArray(draftSpec.skills).map(item => asRecord(item));
  const promptModules = getArray(draftSpec.prompt_modules).map(item => asRecord(item));
  const mcpServers = getArray(draftSpec.mcp_servers).map(item => asRecord(item));
  const platforms = getArray(draftSpec.platforms).map(platformName).filter(Boolean);
  const promptSource = getString(asRecord(assignments.prompt_module_source).source) || 'Unavailable';
  const availableArtifacts = asRecord(config.available_artifacts);

  return {
    runtimeRows: [
      ['Agent type', getString(agent.type) || getString(persona.agent_type) || 'Unavailable'],
      ['Runtime provider/model', providerModelText(activeProviderName, activeModel)],
      ['Provider catalog', providerCatalogText(providers)],
      ['Thinking', thinkingText(activeProvider, options)],
      ['Token budget', tokenBudgetText(options, display)],
      ['Persona style', styleText(style)],
    ],
    consoleRows: [
      ['Display mode', getString(display.mode) || 'Inherit'],
      ['Route debug', triStateText(draftSpec.display_route_debug)],
      ['Platform display overrides', platformDisplayOverridesText(platformOverrides)],
      ['Disabled commands', stringListText(getArray(draftSpec.disabled_commands))],
      ['Disabled built-in prompts', stringListText(getArray(draftSpec.disabled_builtin_prompts))],
      ['Session cleanup', sessionCleanupText(asRecord(draftSpec.session_cleanup))],
    ],
    bindingRows: [
      ['Skill bindings', enabledCountText(skills)],
      ['Prompt bindings', `${enabledCountText(promptModules)} · source ${promptSource}`],
      ['MCP bindings', enabledCountText(mcpServers)],
      ['Skill binding controls', 'Contract gap: use Draft JSON until preview+confirmation editor exists'],
      ['MCP binding controls', 'Contract gap: use Draft JSON until preview+confirmation editor exists'],
      ['Platform bindings', platforms.length ? platforms.join(', ') : 'Unavailable'],
      ['Available artifacts', availableArtifactsText(availableArtifacts)],
    ],
    layerRows: buildLayerRows({ activeProviderName, activeModel, platformDefaults, platformOverrides }),
    configJson: redactedJson({
      manifest: config.manifest,
      draft: config.draft,
      status: sources.status,
      preview: sources.preview,
    }, { redactPromptContent: true }),
    draftJson: redactedJson(draft, { redactPromptContent: true }),
  };
}

function buildRuntimeEvidenceRows(status: Record<string, unknown>): Array<[string, string]> {
  const runtimeEffect = asRecord(status.runtime_effect);
  const divergences = getArray(status.divergences).map(item => asRecord(item));
  return [
    ['Sync status', getString(status.sync_status) || 'Unavailable'],
    ['Runtime loaded', yesNo(runtimeEffect.runtime_loaded)],
    ['Live applied', yesNo(runtimeEffect.live_applied)],
    ['Restart required', yesNo(runtimeEffect.restart_required)],
    ['Restart fields', stringListText(getArray(runtimeEffect.restart_required_fields))],
    ['Runtime effect', getString(runtimeEffect.message) || 'Unavailable'],
    ['Divergences', divergenceText(divergences)],
  ];
}

function yesNo(value: unknown) {
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  return 'Unavailable';
}

function divergenceText(divergences: Array<Record<string, unknown>>) {
  if (!divergences.length) return 'None';
  return divergences.map(divergence => {
    const kind = getString(divergence.type) || getString(divergence.field) || 'divergence';
    const message = getString(divergence.message) || getString(divergence.reason) || 'No detail returned';
    return `${kind}: ${message}`;
  }).join(' · ');
}

function providerModelText(provider: string, model: string) {
  if (!provider && !model) return 'Unavailable';
  return `${provider || 'inherit'} / ${model || 'inherit'}`;
}

function providerCatalogText(providers: Array<Record<string, unknown>>) {
  if (!providers.length) return 'Unavailable';
  const names = providers.map(provider => getString(provider.name)).filter(Boolean);
  return `${providers.length} ${providers.length === 1 ? 'provider' : 'providers'}${names.length ? `: ${names.join(', ')}` : ''}`;
}

function thinkingText(provider: Record<string, unknown>, options: Record<string, unknown>) {
  const parts = [
    getString(provider.thinking),
    optionText(options, 'model_reasoning_effort', 'reasoning'),
    optionText(options, 'reasoning_effort', 'reasoning'),
    optionText(options, 'thinking_budget_tokens', 'thinking budget'),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Inherit';
}

function tokenBudgetText(options: Record<string, unknown>, display: Record<string, unknown>) {
  const parts = [
    optionText(options, 'token_budget', 'token_budget'),
    optionText(options, 'max_tokens', 'max_tokens'),
    optionText(options, 'max_output_tokens', 'max_output_tokens'),
    optionText(display, 'thinking_max_len', 'thinking_max_len'),
    optionText(display, 'tool_max_len', 'tool_max_len'),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Inherit';
}

function optionText(record: Record<string, unknown>, key: string, label: string) {
  const value = record[key];
  if (value === undefined || value === null || value === '') return '';
  return `${label} ${String(value)}`;
}

function styleText(style: Record<string, unknown>) {
  const parts = [];
  const preferences = getArray(style.preferences);
  if (getString(style.voice)) parts.push(`voice ${getString(style.voice)}`);
  if (preferences.length) parts.push(`${preferences.length} ${preferences.length === 1 ? 'preference' : 'preferences'}`);
  if (getString(style.custom_instructions)) parts.push('custom instructions configured');
  return parts.length ? parts.join(' · ') : 'Inherit';
}

function triStateText(value: unknown) {
  if (value === true || value === 'true') return 'Enabled';
  if (value === false || value === 'false') return 'Disabled';
  return 'Inherit';
}

function platformDisplayOverridesText(overrides: Record<string, unknown>) {
  const rows = Object.entries(overrides)
    .map(([platform, override]) => {
      const mode = getString(asRecord(override).display_mode);
      return mode ? `${platform}: ${mode}` : '';
    })
    .filter(Boolean);
  return rows.length ? rows.join(' · ') : 'None';
}

function stringListText(items: unknown[]) {
  const values = items.map(String).filter(Boolean);
  return values.length ? values.join(', ') : 'None';
}

function sessionCleanupText(cleanup: Record<string, unknown>) {
  if (!Object.keys(cleanup).length) return 'Inherit';
  const enabled = cleanup.enabled === undefined ? 'Inherit' : triStateText(cleanup.enabled);
  const inactiveDays = cleanup.inactive_days === undefined ? 'inactive inherit' : `inactive ${cleanup.inactive_days} days`;
  const keepLatest = cleanup.keep_latest === undefined ? 'keep latest inherit' : `keep latest ${cleanup.keep_latest}`;
  return `${enabled} · ${inactiveDays} · ${keepLatest}`;
}

function enabledCountText(refs: Array<Record<string, unknown>>) {
  const enabledCount = refs.filter(ref => ref.enabled !== false && ref.enabled !== 'false').length;
  return `${enabledCount}/${refs.length} enabled`;
}

function availableArtifactsText(availableArtifacts: Record<string, unknown>) {
  const skillCount = getArray(availableArtifacts.skills).length + getArray(availableArtifacts['config:skills']).length;
  const promptCount = getArray(availableArtifacts.prompt_modules).length + getArray(availableArtifacts['config:prompt_modules']).length;
  const mcpCount = getArray(availableArtifacts.mcp_definitions).length
    + getArray(availableArtifacts.mcp_servers).length
    + getArray(availableArtifacts['config:mcp_definitions']).length;
  return `${skillCount} skills · ${promptCount} prompts · ${mcpCount} MCPs`;
}

function buildLayerRows({
  activeProviderName,
  activeModel,
  platformDefaults,
  platformOverrides,
}: {
  activeProviderName: string;
  activeModel: string;
  platformDefaults: Record<string, unknown>;
  platformOverrides: Record<string, unknown>;
}) {
  const rows: Array<[string, string]> = [
    ['Default runtime', providerModelText(activeProviderName, activeModel)],
  ];
  for (const [platform, defaults] of Object.entries(platformDefaults)) {
    const defaultRecord = asRecord(defaults);
    rows.push([`${platform} runtime override`, providerModelText(getString(defaultRecord.provider), getString(defaultRecord.model))]);
  }
  for (const [platform, override] of Object.entries(platformOverrides)) {
    const overrideRecord = asRecord(override);
    const promptModules = getArray(overrideRecord.prompt_modules).map(String).filter(Boolean);
    const displayMode = getString(overrideRecord.display_mode);
    if (promptModules.length) rows.push([`${platform} prompt override`, promptModules.join(', ')]);
    if (displayMode) rows.push([`${platform} display override`, displayMode]);
  }
  return rows;
}

function platformName(value: unknown) {
  if (typeof value === 'string') return value;
  const record = asRecord(value);
  return getString(record.type) || getString(record.name);
}

function mapSessionRow(rowValue: unknown) {
  const row = asRecord(rowValue);
  const profile = asRecord(row.runtime_profile);
  return {
    id: getString(row.id),
    name: getString(row.name) || 'Unnamed session',
    platform: getString(row.platform) || 'unknown',
    active: getBool(row.active),
    live: getBool(row.live),
    historyCount: getNumber(row.history_count),
    runtimeProfileId: getString(profile.id),
    codexThreadId: getString(profile.codex_thread_id),
    updatedAt: getString(row.updated_at),
  };
}

function extractPlatforms(value: unknown) {
  return filterRealPersonaPlatforms(getArray(value).map(item => {
    if (typeof item === 'string') return item;
    return asRecord(item).name;
  }));
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function latestVersionNumber(versions: Array<Record<string, unknown>>) {
  return versions.reduce((latest, version) => Math.max(latest, getNumber(version.version)), 0);
}

function getString(value: unknown) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function formatScopeLabel(platform: string) {
  return platform.trim() || 'Default';
}

function getNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function getBool(value: unknown) {
  return value === true || value === 'true';
}
